import { AlternarDaBarra, BarraDeLista, BuscaDaBarra, FiltrosAtivos } from '../BarraDeLista';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ErroApi } from '../api';
import { data, telefoneDigitadoLegivel, telefoneLegivel } from '../formato';
import { CadastroRapidoCliente, type Cliente as ClienteResumo } from '../cadastros';
import { esquecerClientes } from '../Filtros';
import { Lixeira, useLixeira } from '../Lixeira';
import { TrilhaDaFicha } from '../Trilha';
import {
  Area, Campo, Carregando, Dialogo, ErroDaApi, Escolha, Selo, Vazio,
} from '../ui';
import type { Tom } from '../ui';
import { useSessao } from '../sessao';
import { useCaminho } from '../Caminho';
import {
  CamposDaFicha, TabelaComFicha, type ColunaComFicha, type FichaDaLinha,
} from '../TabelaComFicha';
import type { InicialDaNovaConversa } from './comercial/JanelaNovaConversa';
import { celularDoTelefone } from './comercial/JanelasDaConversa';

/* A Nova conversa do Comercial (onda 554, Tarefa 8): só desce quando alguém chama no WhatsApp. */
const JanelaNovaConversa = lazy(() => import('./comercial/JanelaNovaConversa')
  .then((m) => ({ default: m.JanelaNovaConversa })));

/**
 * Clientes, obras e contatos — o cadastro que faltava.
 *
 * ── Por que esta tela existe ─────────────────────────────────────────
 *
 * A API de clientes é da `0001` e foi a primeira a provar a cadeia
 * inteira (HTTP → guarda → transação → gatilho → trilha). A interface,
 * porém, só ganhou o cadastro RÁPIDO (`cadastros.tsx`) — o diálogo que
 * desfaz o beco do passo 1 do recebimento. Ele é a resposta certa para
 * «chegou material de cliente novo» e continua sendo; o que ele não
 * responde é a pergunta de quem administra: «quais clientes temos?»,
 * «este CNPJ está certo?», «quem é o contato desta obra?».
 *
 * «O cadastro de itens está ocupando muito do menu lateral. Acredito
 *  que deva ter um local centralizado para cadastrar coisas, como
 *  cliente, fornecedores de calibração, locais de armazenamento»
 * (Humberto, 08/09/2026). Esta é a primeira porta atrás de «Cadastros».
 *
 * ── O que ela NÃO faz ────────────────────────────────────────────────
 *
 * Não exclui (CA-22): cliente com OS no histórico continua no histórico.
 * O que muda é a situação — inativo some da busca do recebimento e da
 * OS sem sumir de nenhum relatório já emitido. E o número da obra não se
 * troca depois de criado: ele é o número da OS, e já saiu impresso.
 */

interface Cliente {
  id: string;
  codigo: string | null;
  razao_social: string;
  nome_fantasia: string | null;
  cnpj_cpf: string | null;
  status: string;
}

interface Obra {
  id: string;
  codigo: string;
  cliente_id: string;
  nome: string;
  endereco?: string | null;
  municipio: string | null;
  uf: string | null;
  status: string;
}

interface Contato {
  id: string;
  cliente_id: string | null;
  obra_id: string | null;
  nome: string;
  cargo: string | null;
  email: string | null;
  telefone: string | null;
  principal: boolean;
  status: string;
  /** O WhatsApp normalizado e o pedido de parar (onda 554, Tarefa 8). */
  whatsapp?: string | null;
  nao_receber_em?: string | null;
}

const TOM: Record<string, Tom> = { ativo: 'ok', inativo: 'neutro', bloqueado: 'critico' };
const SITUACAO: Record<string, string> = {
  ativo: 'Ativo', inativo: 'Inativo', bloqueado: 'Bloqueado',
};

/** 14 dígitos viram CNPJ, 11 viram CPF; qualquer outra coisa sai como veio. */
export function documento(v: string | null | undefined): string {
  if (!v) return '';
  const d = v.replace(/\D/g, '');
  if (d.length === 14) {
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  }
  if (d.length === 11) {
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  }
  return v;
}

// ── A lista ──────────────────────────────────────────────────────────

export function Clientes() {
  const [linhas, setLinhas] = useState<Cliente[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [busca, setBusca] = useState('');
  const [verInativos, setVerInativos] = useState(false);
  const [novo, setNovo] = useState(false);
  const navegar = useNavigate();
  const { pode } = useSessao();

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const q = new URLSearchParams();
      if (busca.trim()) q.set('busca', busca.trim());
      setLinhas(await api<Cliente[]>(`/clientes?${q}`));
    } catch (e) { setErro(e); }
  }, [busca]);

  useEffect(() => {
    const t = setTimeout(() => { void carregar(); }, 250);
    return () => clearTimeout(t);
  }, [carregar]);

  /*
   * A API não filtra por situação — a lista é curta (LIMIT 200) e a
   * conta é da tela. Inativo fica escondido por padrão: quem abre esta
   * lista quer o cliente com quem trabalha, não o que fechou as portas.
   */
  const visiveis = (linhas ?? []).filter((c) => verInativos || c.status !== 'inativo');
  const escondidos = (linhas ?? []).length - visiveis.length;

  return (
    <div>
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Clientes e obras</h1>
          <p className="sub">
            Quem contrata, as obras de cada um e os contatos de cada obra — por razão social
          </p>
        </div>
        {pode('cadastro.editar') && (
          <button type="button" className="botao" onClick={() => setNovo(true)}>
            Novo cliente
          </button>
        )}
      </div>

      {/*
        * O MESMO diálogo do recebimento e da OS. Um segundo formulário
        * de cliente seria a garantia de que um dia um exigiria CNPJ e o
        * outro não. Criado, a ficha abre em seguida — é nela que se
        * cadastram as obras e os contatos.
        */}
      <CadastroRapidoCliente
        aberto={novo}
        nomeDigitado={busca.trim()}
        aoFechar={() => setNovo(false)}
        aoCriar={(c: ClienteResumo) => {
          setNovo(false);
          esquecerClientes();
          navegar(`/cliente/${c.id}`);
        }}
      />

      {/* A barra do padrão (onda 273): a caixa «Mostrar inativos» virou alternância. */}
      <BarraDeLista rotulo="Filtros dos clientes">
        <BuscaDaBarra valor={busca} aoMudar={setBusca}
          placeholder="Razão social ou nome fantasia" rotulo="Buscar cliente" />
        <AlternarDaBarra ligado={verInativos} aoMudar={setVerInativos}>
          {verInativos ? '✓ Com os inativos' : `Mostrar inativos${escondidos > 0 ? ` (${escondidos})` : ''}`}
        </AlternarDaBarra>
      </BarraDeLista>
      <FiltrosAtivos
        itens={busca.trim() ? [{ chave: 'q', texto: `Busca: ${busca.trim()}`, tirar: () => setBusca('') }] : []}
        aoLimpar={() => setBusca('')}
        contagem={linhas ? `${visiveis.length} ${visiveis.length === 1 ? 'cliente' : 'clientes'}` : undefined} />

      {erro ? <ErroDaApi erro={erro} />
        : !linhas ? <Carregando />
        : (
          <div className="cartao">
            <div className="cartao-cabeca">
              <h2>Cadastro</h2>
              <span className="sub">{visiveis.length} cliente(s)</span>
            </div>

            {visiveis.length === 0 ? (
              <Vazio>
                {busca.trim()
                  ? 'Nenhum cliente com esse nome. Se é cliente novo, cadastre — a busca do recebimento e da OS passa a encontrá-lo.'
                  : 'Nenhum cliente cadastrado ainda.'}
              </Vazio>
            ) : (
              /*
               * Os clientes numa tabela com ficha (onda 219): o cliente e a
               * situação ficam; o documento é detalhe e, com a ficha aberta,
               * mora nela — junto das obras do cliente, que antes só se
               * viam abrindo o cadastro. O nome continua levando ao cadastro.
               */
              <TabelaComFicha
                linhas={visiveis}
                chave={(c) => c.id}
                nome={(c) => c.razao_social}
                rotulo="Clientes"
                colunas={COLUNAS_DO_CLIENTE}
                ficha={fichaDoCliente}
                lixeira={{ tipo: 'cliente', id: (c) => c.id, rotulo: (c) => c.razao_social,
                  aoApagar: () => { esquecerClientes(); void carregar(); } }}
              />
            )}

            <p className="rodape-regra">
              Cliente não se exclui: quem já teve OS fica no histórico
              dela. Inativar tira o cliente da busca do recebimento e da
              abertura de OS — e nada mais.
            </p>
          </div>
        )}
    </div>
  );
}

// ── A ficha ──────────────────────────────────────────────────────────

export function ClienteFicha() {
  const { id = '' } = useParams();
  const [c, setC] = useState<Cliente | null>(null);
  useCaminho(c ? [{ rotulo: c.razao_social }] : null);
  const [obras, setObras] = useState<Obra[] | null>(null);
  const [contatos, setContatos] = useState<Contato[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [editando, setEditando] = useState(false);
  const [inativando, setInativando] = useState(false);
  const [novaObra, setNovaObra] = useState(false);
  const [novoContato, setNovoContato] = useState(false);
  const [versao, setVersao] = useState(0);
  /* «Chamar no WhatsApp» (onda 554, Tarefa 8; spec §3.5, quadro 5). */
  const [perguntaDoCelular, setPerguntaDoCelular] = useState<Contato | null>(null);
  const [chamar, setChamar] = useState<InicialDaNovaConversa | null>(null);
  const { pode } = useSessao();
  const navegar = useNavigate();
  /* O botão é de quem responde no atendimento (decisão do controlador da onda 554): a engenharia vê a ficha sem ele. */
  const chama = pode('atendimento.responder');
  const comLixeira = useLixeira();

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const cliente = await api<Cliente | null>(`/clientes/${id}`);
      setC(cliente);
      if (!cliente) return;
      const [os, contatosDoCliente] = await Promise.all([
        api<Obra[]>(`/obras?cliente=${id}`),
        api<Contato[]>(`/contatos?cliente=${id}`),
      ]);
      setObras(os);
      /*
       * O contato costuma ser DA OBRA (`contato.controller.ts`), e a
       * API filtra por um dono de cada vez. Então a ficha pergunta pelo
       * cliente e por cada obra dele, e junta — sem repetir quem está
       * ligado aos dois.
       */
      const porObra = await Promise.all(
        os.map((o) => api<Contato[]>(`/contatos?obra=${o.id}`)));
      const vistos = new Set<string>();
      const todos: Contato[] = [];
      for (const lista of [contatosDoCliente, ...porObra]) {
        for (const ct of lista) {
          if (vistos.has(ct.id)) continue;
          vistos.add(ct.id);
          todos.push(ct);
        }
      }
      setContatos(todos);
      setVersao((v) => v + 1);
    } catch (e) { setErro(e); }
  }, [id]);

  useEffect(() => { void carregar(); }, [carregar]);

  if (erro) return <ErroDaApi erro={erro} />;
  if (c === null && obras === null) return <Carregando />;
  if (!c) {
    return (
      <div>
        <Vazio>Este cliente não existe — ou o endereço veio errado.</Vazio>
        <Link to="/clientes" className="botao discreto">Voltar aos clientes</Link>
      </div>
    );
  }

  const nomeDaObra = (obraId: string | null) =>
    obras?.find((o) => o.id === obraId);

  return (
    <div>
      <div className="linha-topo">
        <div className="cresce">
          <p className="sub" style={{ margin: 0 }}>
            <Link to="/cadastros">Cadastros</Link> › <Link to="/clientes">Clientes e obras</Link>
          </p>
          <h1>{c.razao_social}</h1>
          <p className="sub">
            {c.nome_fantasia ? `${c.nome_fantasia} · ` : ''}
            {c.cnpj_cpf ? documento(c.cnpj_cpf) : 'CNPJ/CPF não informado'}
            {c.codigo ? ` · ${c.codigo}` : ''}
          </p>
        </div>
        <Selo tom={TOM[c.status] ?? 'neutro'}>{SITUACAO[c.status] ?? c.status}</Selo>
        {pode('cadastro.editar') && (
          <button type="button" className="botao secundario" onClick={() => setEditando(true)}>
            Corrigir dados
          </button>
        )}
        {pode('cadastro.inativar') && c.status === 'ativo' && (
          <button type="button" className="botao discreto" onClick={() => setInativando(true)}>
            Inativar
          </button>
        )}
      </div>

      {editando && (
        <FormCliente
          c={c}
          aoFechar={() => setEditando(false)}
          aoPronto={() => { setEditando(false); esquecerClientes(); void carregar(); }}
        />
      )}
      {inativando && (
        <FormInativar
          c={c}
          aoFechar={() => setInativando(false)}
          aoPronto={() => { setInativando(false); esquecerClientes(); void carregar(); }}
        />
      )}

      {/* ── Obras ──────────────────────────────────────────────────── */}
      <div className="cartao">
        <div className="cartao-cabeca">
          <h2>Obras</h2>
          <span className="sub">{obras?.length ?? 0} obra(s)</span>
          {pode('cadastro.editar') && c.status === 'ativo' && (
            <button type="button" className="botao secundario" onClick={() => setNovaObra(true)}>
              Nova obra
            </button>
          )}
        </div>
        {!obras ? <Carregando /> : obras.length === 0 ? (
          <Vazio>
            Nenhuma obra ainda. A obra também pode nascer na abertura da OS —
            aqui é para quem quer deixá-la pronta antes.
          </Vazio>
        ) : (
          <div className="rola">
            <table>
              <thead>
                <tr>
                  <th scope="col">Número</th>
                  <th scope="col">Obra</th>
                  <th scope="col" className="col-opcional">Local</th>
                  <th scope="col">Situação</th>
                  {comLixeira && <th scope="col" className="col-lixeira" aria-label="Excluir" />}
                </tr>
              </thead>
              <tbody>
                {obras.map((o) => (
                  <tr key={o.id}>
                    <th scope="row"><span className="mono">{o.codigo}</span></th>
                    <td>{o.nome}</td>
                    <td className="col-opcional">
                      {[o.municipio, o.uf].filter(Boolean).join(' – ')
                        || <span className="nada">—</span>}
                    </td>
                    <td>
                      <Selo tom={TOM[o.status] ?? 'neutro'}>
                        {SITUACAO[o.status] ?? o.status}
                      </Selo>
                    </td>
                    {comLixeira && (
                      <td className="col-lixeira">
                        <Lixeira tipo="obra" id={o.id} rotulo={`obra ${o.codigo}`}
                          aoApagar={() => { void carregar(); }} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="rodape-regra">
          O número da obra é o número da OS (AAMMNNN) e não se troca
          depois de criado — ele já saiu impresso.
        </p>
      </div>

      {novaObra && (
        <FormObra
          clienteId={c.id}
          aoFechar={() => setNovaObra(false)}
          aoPronto={() => { setNovaObra(false); void carregar(); }}
        />
      )}

      {/* ── Contatos ───────────────────────────────────────────────── */}
      <div className="cartao">
        <div className="cartao-cabeca">
          <h2>Contatos</h2>
          <span className="sub">{contatos?.length ?? 0} contato(s)</span>
          {pode('cadastro.editar') && c.status === 'ativo' && (
            <button type="button" className="botao secundario" onClick={() => setNovoContato(true)}>
              Novo contato
            </button>
          )}
        </div>
        {!contatos ? <Carregando /> : contatos.length === 0 ? (
          <Vazio>
            Nenhum contato. O contato costuma ser da obra — o engenheiro
            que acompanha aquela obra —, e pode ser do cliente como um todo.
          </Vazio>
        ) : (
          <div className="rola">
            <table>
              <thead>
                <tr>
                  <th scope="col">Contato</th>
                  <th scope="col">Como falar</th>
                  <th scope="col" className="col-opcional">De</th>
                  {chama && <th scope="col"><span className="so-leitor">Chamar no WhatsApp</span></th>}
                </tr>
              </thead>
              <tbody>
                {contatos.filter((ct) => ct.status !== 'inativo').map((ct) => {
                  const obra = nomeDaObra(ct.obra_id);
                  return (
                    <tr key={ct.id}>
                      <th scope="row">
                        <strong>{ct.nome}{ct.principal ? ' ★' : ''}</strong>
                        {ct.cargo && <span className="sub">{ct.cargo}</span>}
                      </th>
                      <td>
                        {ct.whatsapp && <span className="sub mono">WhatsApp {telefoneLegivel(ct.whatsapp)}</span>}
                        {ct.email && <span className="sub">{ct.email}</span>}
                        {ct.telefone && <span className="sub mono">{telefoneDigitadoLegivel(ct.telefone)}</span>}
                        {!ct.email && !ct.telefone && !ct.whatsapp && <span className="nada">sem meio de contato</span>}
                      </td>
                      <td className="col-opcional">
                        {obra
                          ? <><span className="mono">{obra.codigo}</span><span className="sub">{obra.nome}</span></>
                          : <span className="sub">do cliente</span>}
                      </td>
                      {chama && (
                        <td className="contato-chamar">
                          <ChamarOContato ct={ct} aoChamar={() => {
                            if (ct.whatsapp) setChamar({ contatoId: ct.id });
                            else if (celularDoTelefone(ct.telefone)) setPerguntaDoCelular(ct);
                            else setChamar({ contatoId: ct.id });
                          }} />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {perguntaDoCelular && (
        <PerguntaDoCelular ct={perguntaDoCelular} gravaOCadastro={pode('cadastro.editar')}
          aoFechar={() => setPerguntaDoCelular(null)}
          aoContinuar={(inicial, gravou) => {
            setPerguntaDoCelular(null);
            if (gravou) void carregar();
            setChamar(inicial);
          }} />
      )}
      {chamar && (
        <Suspense fallback={null}>
          <JanelaNovaConversa aberta inicial={chamar} aoFechar={() => setChamar(null)}
            aoAbrir={(conversaId, rascunho) => {
              setChamar(null);
              navegar(`/comercial/conversa/${conversaId}`, rascunho ? { state: { rascunho } } : undefined);
            }} />
        </Suspense>
      )}

      {novoContato && (
        <FormContato
          clienteId={c.id}
          obras={obras ?? []}
          aoFechar={() => setNovoContato(false)}
          aoPronto={() => { setNovoContato(false); void carregar(); }}
        />
      )}

      <TrilhaDaFicha
        key={`trilha-${versao}`}
        rota={`/clientes/${c.id}/trilha`}
        titulo="Trilha de alterações do cliente"
        explica="Quem cadastrou e quem corrigiu o quê — razão social, CNPJ, situação — com o valor anterior, o novo e o motivo."
      />
    </div>
  );
}

// ── Chamar no WhatsApp (onda 554, Tarefa 8) ──────────────────────────

/**
 * O botão da linha do contato (spec §3.5, quadro 5): com WhatsApp, abre a
 * Nova conversa com o contato; sem WhatsApp e com celular, pergunta se o
 * telefone serve; só com fixo (ou nada), a Nova conversa pede o número. Quem
 * pediu para parar (B7) fica com o botão indisponível, com o motivo e a data.
 */
function ChamarOContato({ ct, aoChamar }: { ct: Contato; aoChamar(): void }) {
  if (ct.nao_receber_em) {
    const motivo = `Pediu para não receber mensagens em ${data(ct.nao_receber_em)}`;
    return (
      <>
        <button type="button" className="botao secundario" aria-disabled="true" aria-describedby={`parar-${ct.id}`}>
          Chamar no WhatsApp
        </button>
        <span className="sub contato-parou" id={`parar-${ct.id}`}>{motivo}</span>
      </>
    );
  }
  const fixo = !ct.whatsapp && !celularDoTelefone(ct.telefone);
  return (
    <>
      <button type="button" className="botao secundario" onClick={aoChamar}>Chamar no WhatsApp</button>
      {fixo && (
        <span className="sub">{ct.telefone ? 'Telefone fixo: pede o WhatsApp' : 'Sem telefone: pede o WhatsApp'}</span>
      )}
    </>
  );
}

/**
 * «Usar o telefone como WhatsApp?»: o contato não tem WhatsApp, e o telefone
 * é de celular (correção 1 da Tarefa 8, decisão do controlador).
 *  · Quem edita o cadastro lê o que vai acontecer — «O número … fica gravado
 *    como WhatsApp de <nome>.» —, e «Usar e continuar» grava
 *    (`PATCH /comercial/contatos/:id`, que normaliza e confere o duplicado) e
 *    segue com o contato. Não há caixa para desmarcar: a Nova conversa grava
 *    o telefone como WhatsApp do contato de qualquer jeito.
 *  · Quem não edita o cadastro lê o motivo de antemão, e «Usar e continuar»
 *    fica indisponível com ele (P-UI-25): a API recusaria ao enviar.
 * «Outro número» abre a Nova conversa com o contato, pedindo o número.
 */
function PerguntaDoCelular({ ct, gravaOCadastro, aoFechar, aoContinuar }: {
  ct: Contato;
  gravaOCadastro: boolean;
  aoFechar(): void;
  aoContinuar(inicial: InicialDaNovaConversa, gravou: boolean): void;
}) {
  const celular = celularDoTelefone(ct.telefone)!;
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const primeiro = ct.nome.trim().split(/\s+/)[0];
  const idMotivo = `motivo-celular-${ct.id}`;
  const continuar = async () => {
    if (ocupado || !gravaOCadastro) return;
    setOcupado(true);
    setErro(null);
    try {
      await api(`/comercial/contatos/${ct.id}`, { metodo: 'PATCH', corpo: { whatsapp: celular } });
      aoContinuar({ contatoId: ct.id }, true);
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  };
  return (
    <Dialogo aberto titulo="Usar o telefone como WhatsApp?" rotulo={ct.nome} aoFechar={aoFechar} classe="pergunta-do-celular">
      <p>
        {primeiro} não tem WhatsApp cadastrado. O telefone <span className="mono">{telefoneLegivel(celular)}</span> é de
        celular.
      </p>
      {gravaOCadastro ? (
        <p>O número <span className="mono">{telefoneLegivel(celular)}</span> fica gravado como WhatsApp de {ct.nome}.</p>
      ) : (
        <p className="o-que-falta" id={idMotivo}>
          {ct.nome} é um contato do cadastro de clientes: quem grava o WhatsApp dele é quem edita Clientes e obras. Peça
          a essa pessoa para gravar o número, ou use «Outro número».
        </p>
      )}
      <ErroDaApi erro={erro} />
      <div className="rodape-dialogo">
        <button type="button" className="botao secundario" onClick={() => aoContinuar({ contatoId: ct.id }, false)}>
          Outro número
        </button>
        <button type="button" className="botao" aria-disabled={ocupado || !gravaOCadastro}
          aria-describedby={gravaOCadastro ? undefined : idMotivo} onClick={() => void continuar()}>
          {ocupado ? 'Gravando…' : 'Usar e continuar'}
        </button>
      </div>
    </Dialogo>
  );
}

// ── Corrigir os dados ────────────────────────────────────────────────

/**
 * O PATCH de cliente usa `COALESCE`: campo vazio no formulário quer
 * dizer «não mexa», e não «apague». Por isso o formulário manda só o
 * que mudou — e exige motivo, porque a trilha sem motivo diz o quê e
 * não o porquê.
 */
function FormCliente({
  c, aoFechar, aoPronto,
}: { c: Cliente; aoFechar(): void; aoPronto(): void }) {
  const [razaoSocial, setRazaoSocial] = useState(c.razao_social);
  const [nomeFantasia, setNomeFantasia] = useState(c.nome_fantasia ?? '');
  const [documentoDigitado, setDocumentoDigitado] = useState(c.cnpj_cpf ?? '');
  const [codigo, setCodigo] = useState(c.codigo ?? '');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);

  const soDigitos = documentoDigitado.replace(/\D/g, '');
  const docValido = soDigitos.length === 0
    || soDigitos.length === 11 || soDigitos.length === 14;
  const erroApi = erro instanceof ErroApi ? erro : null;

  const mudou = razaoSocial.trim() !== c.razao_social
    || nomeFantasia.trim() !== (c.nome_fantasia ?? '')
    || soDigitos !== (c.cnpj_cpf ?? '')
    || codigo.trim() !== (c.codigo ?? '');

  async function salvar() {
    setErro(null);
    setSalvando(true);
    try {
      await api(`/clientes/${c.id}`, {
        metodo: 'PATCH',
        corpo: {
          razaoSocial: razaoSocial.trim() !== c.razao_social ? razaoSocial.trim() : undefined,
          nomeFantasia: nomeFantasia.trim() !== (c.nome_fantasia ?? '') && nomeFantasia.trim()
            ? nomeFantasia.trim() : undefined,
          cnpjCpf: soDigitos !== (c.cnpj_cpf ?? '') && soDigitos ? soDigitos : undefined,
          codigo: codigo.trim() !== (c.codigo ?? '') && codigo.trim() ? codigo.trim() : undefined,
          motivo: motivo.trim(),
        },
      });
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setSalvando(false); }
  }

  return (
    <Dialogo aberto titulo="Corrigir dados do cliente" aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <Campo
        rotulo="Razão social"
        value={razaoSocial}
        onChange={(e) => setRazaoSocial(e.target.value)}
        erro={erroApi?.campo('razaoSocial')}
        dica="Como aparece no contrato e no relatório."
        autoFocus
      />
      <Campo
        rotulo="Nome fantasia"
        value={nomeFantasia}
        onChange={(e) => setNomeFantasia(e.target.value)}
        erro={erroApi?.campo('nomeFantasia')}
      />
      <div className="linha">
        <Campo
          rotulo="CNPJ ou CPF"
          inputMode="numeric"
          value={documentoDigitado}
          onChange={(e) => setDocumentoDigitado(e.target.value)}
          erro={erroApi?.campo('cnpjCpf')
            ?? (!docValido ? 'CNPJ tem 14 dígitos e CPF tem 11.' : undefined)}
        />
        <Campo
          rotulo="Código interno"
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          erro={erroApi?.campo('codigo')}
          dica="Opcional. O código que a Testesolo usa para este cliente."
        />
      </div>
      <Area
        rotulo="Motivo da correção"
        rows={2}
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        erro={erroApi?.campo('motivo')}
        dica="Fica na trilha, ao lado do valor antigo e do novo."
      />
      <div className="linha" style={{ marginTop: '1rem' }}>
        <button
          type="button" className="botao"
          disabled={!mudou || razaoSocial.trim().length < 3 || !docValido
            || motivo.trim().length < 5 || salvando}
          onClick={() => { void salvar(); }}
        >
          {salvando ? 'Salvando…' : 'Salvar correção'}
        </button>
        <button type="button" className="botao secundario" onClick={aoFechar} disabled={salvando}>
          Cancelar
        </button>
      </div>
      <p className="dica" style={{ marginTop: '.7rem' }}>
        Campo deixado em branco não apaga o valor que já existe — o
        cadastro só troca o que você preencheu.
      </p>
    </Dialogo>
  );
}

function FormInativar({
  c, aoFechar, aoPronto,
}: { c: Cliente; aoFechar(): void; aoPronto(): void }) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);

  async function inativar() {
    setErro(null);
    setSalvando(true);
    try {
      await api(`/clientes/${c.id}/inativar`, {
        metodo: 'PATCH', corpo: { motivo: motivo.trim() },
      });
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setSalvando(false); }
  }

  return (
    <Dialogo aberto titulo={`Inativar ${c.razao_social}`} aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <p>
        O cliente some da busca do recebimento e da abertura de OS. Tudo o
        que já existe em nome dele — OS, amostras, laudos, medições —
        continua onde está.
      </p>
      <Area
        rotulo="Motivo"
        rows={3}
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        dica="Fica na trilha do cadastro."
        autoFocus
      />
      <div className="linha" style={{ marginTop: '1rem' }}>
        <button
          type="button" className="botao perigo"
          disabled={motivo.trim().length < 5 || salvando}
          onClick={() => { void inativar(); }}
        >
          {salvando ? 'Inativando…' : 'Inativar cliente'}
        </button>
        <button type="button" className="botao secundario" onClick={aoFechar} disabled={salvando}>
          Cancelar
        </button>
      </div>
    </Dialogo>
  );
}

// ── Nova obra ────────────────────────────────────────────────────────

/**
 * A mesma obra que nasce na abertura da OS (`OsForms.tsx`), com o
 * cliente já escolhido — é a ficha dele. O código é validado nos dois
 * lugares, aqui e no banco (`obra_codigo_numero_do_projeto`).
 */
function FormObra({
  clienteId, aoFechar, aoPronto,
}: { clienteId: string; aoFechar(): void; aoPronto(): void }) {
  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState('');
  const [endereco, setEndereco] = useState('');
  const [municipio, setMunicipio] = useState('');
  const [uf, setUf] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);

  const codigoOk = /^[0-9]{2}(0[1-9]|1[0-2])[1-9][0-9]{2}$/.test(codigo.trim());
  const erroApi = erro instanceof ErroApi ? erro : null;

  async function salvar() {
    setErro(null);
    setSalvando(true);
    try {
      await api('/obras', {
        metodo: 'POST',
        corpo: {
          codigo: codigo.trim(),
          clienteId,
          nome: nome.trim(),
          endereco: endereco.trim() || undefined,
          municipio: municipio.trim() || undefined,
          uf: uf.trim() ? uf.trim().toUpperCase() : undefined,
        },
      });
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setSalvando(false); }
  }

  return (
    <Dialogo aberto titulo="Nova obra" aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <Campo
        rotulo="Número da obra"
        inputMode="numeric"
        value={codigo}
        onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 7))}
        erro={erroApi?.campo('codigo')
          ?? (codigo.length === 7 && !codigoOk
            ? 'Mês tem que ser de 01 a 12 e o sequencial de 100 a 999.'
            : undefined)}
        dica="Sete dígitos, AAMMNNN — ano, mês de abertura e sequencial. Ex.: 2608194. É este número que a OS vai levar."
        autoFocus
      />
      <Campo
        rotulo="Nome da obra"
        value={nome}
        onChange={(e) => setNome(e.target.value)}
        erro={erroApi?.campo('nome')}
        dica="Como aparece no contrato e no cabeçalho do relatório."
      />
      <Campo
        rotulo="Endereço"
        value={endereco}
        onChange={(e) => setEndereco(e.target.value)}
        erro={erroApi?.campo('endereco')}
        dica="Opcional."
      />
      <div className="linha">
        <Campo
          rotulo="Município"
          value={municipio}
          onChange={(e) => setMunicipio(e.target.value)}
          erro={erroApi?.campo('municipio')}
        />
        <Campo
          rotulo="UF"
          value={uf}
          maxLength={2}
          onChange={(e) => setUf(e.target.value.replace(/[^a-zA-Z]/g, ''))}
          erro={erroApi?.campo('uf')}
        />
      </div>
      <div className="linha" style={{ marginTop: '1rem' }}>
        <button
          type="button" className="botao"
          disabled={!codigoOk || nome.trim().length < 3 || salvando}
          onClick={() => { void salvar(); }}
        >
          {salvando ? 'Cadastrando…' : 'Cadastrar obra'}
        </button>
        <button type="button" className="botao secundario" onClick={aoFechar} disabled={salvando}>
          Cancelar
        </button>
      </div>
    </Dialogo>
  );
}

// ── Novo contato ─────────────────────────────────────────────────────

function FormContato({
  clienteId, obras, aoFechar, aoPronto,
}: { clienteId: string; obras: Obra[]; aoFechar(): void; aoPronto(): void }) {
  const [nome, setNome] = useState('');
  const [cargo, setCargo] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [principal, setPrincipal] = useState(false);
  const [obraId, setObraId] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);

  const erroApi = erro instanceof ErroApi ? erro : null;

  async function salvar() {
    setErro(null);
    setSalvando(true);
    try {
      await api('/contatos', {
        metodo: 'POST',
        corpo: {
          /*
           * Contato de obra pertence à OBRA; sem obra, ao cliente. A
           * constraint `contato_tem_dono` exige ao menos um, e é ela —
           * não este formulário — quem garante.
           */
          clienteId: obraId ? undefined : clienteId,
          obraId: obraId ?? undefined,
          nome: nome.trim(),
          cargo: cargo.trim() || undefined,
          email: email.trim() || undefined,
          telefone: telefone.trim() || undefined,
          principal,
        },
      });
      aoPronto();
    } catch (e) { setErro(e); }
    finally { setSalvando(false); }
  }

  return (
    <Dialogo aberto titulo="Novo contato" aoFechar={aoFechar}>
      <ErroDaApi erro={erro} />
      <Campo
        rotulo="Nome"
        value={nome}
        onChange={(e) => setNome(e.target.value)}
        erro={erroApi?.campo('nome')}
        autoFocus
      />
      <Campo
        rotulo="Cargo ou função"
        value={cargo}
        onChange={(e) => setCargo(e.target.value)}
        erro={erroApi?.campo('cargo')}
        dica="Engenheiro da obra, comprador, fiscal…"
      />
      <div className="linha">
        <Campo
          rotulo="E-mail"
          type="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          erro={erroApi?.campo('email')}
        />
        <Campo
          rotulo="Telefone"
          inputMode="tel"
          value={telefone}
          onChange={(e) => setTelefone(e.target.value)}
          erro={erroApi?.campo('telefone')}
        />
      </div>
      {obras.length > 0 && (
        <Escolha
          rotulo="Contato de qual obra"
          opcoes={obras.map((o) => ({ valor: o.id, texto: `${o.codigo} · ${o.nome}` }))}
          valor={obraId}
          aoEscolher={setObraId}
          limpavel
          dica="Deixe em branco se o contato é do cliente como um todo, e não de uma obra."
        />
      )}
      <label className="linha" style={{ alignItems: 'center', gap: '.4rem', marginBottom: '.8rem' }}>
        <input
          type="checkbox"
          checked={principal}
          onChange={(e) => setPrincipal(e.target.checked)}
          style={{ flex: '0 0 auto' }}
        />
        Contato principal
      </label>
      <div className="linha" style={{ marginTop: '.4rem' }}>
        <button
          type="button" className="botao"
          disabled={nome.trim().length < 3 || salvando}
          onClick={() => { void salvar(); }}
        >
          {salvando ? 'Cadastrando…' : 'Cadastrar contato'}
        </button>
        <button type="button" className="botao secundario" onClick={aoFechar} disabled={salvando}>
          Cancelar
        </button>
      </div>
    </Dialogo>
  );
}

const COLUNAS_DO_CLIENTE: ColunaComFicha<Cliente>[] = [
  {
    chave: 'cliente',
    cabeca: <th scope="col">Cliente</th>,
    celula: (c) => (
      <th scope="row">
        <Link to={`/cliente/${c.id}`}>{c.razao_social}</Link>
        {c.nome_fantasia && <span className="sub">{c.nome_fantasia}</span>}
        {c.codigo && <span className="mono">{c.codigo}</span>}
      </th>
    ),
  },
  {
    chave: 'documento', detalhe: true,
    cabeca: <th scope="col" className="col-opcional">CNPJ / CPF</th>,
    celula: (c) => (
      <td className="col-opcional">
        {c.cnpj_cpf
          ? <span className="mono">{documento(c.cnpj_cpf)}</span>
          : <span className="nada">não informado</span>}
      </td>
    ),
  },
  {
    chave: 'situacao',
    cabeca: <th scope="col">Situação</th>,
    celula: (c) => (
      <td><Selo tom={TOM[c.status] ?? 'neutro'}>{SITUACAO[c.status] ?? c.status}</Selo></td>
    ),
  },
];

/**
 * As obras do cliente, na ficha. Carregam quando a ficha abre — uma
 * consulta por ficha aberta, e não uma por linha da lista.
 */
function ObrasDoCliente({ id }: { id: string }) {
  const [obras, setObras] = useState<Obra[] | null>(null);
  useEffect(() => {
    let vivo = true;
    setObras(null);
    api<Obra[]>(`/obras?cliente=${id}`)
      .then((o) => { if (vivo) setObras(o); })
      .catch(() => { if (vivo) setObras([]); });
    return () => { vivo = false; };
  }, [id]);
  if (obras === null) return <span className="nada">carregando…</span>;
  if (obras.length === 0) return <span className="nada">nenhuma obra cadastrada</span>;
  return (
    <ul className="tf-lista">
      {obras.map((o) => (
        <li key={o.id}>
          <span className="mono">{o.codigo}</span> · {o.nome}
          {(o.municipio || o.uf) && (
            <span className="sub">{[o.municipio, o.uf].filter(Boolean).join(' — ')}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function fichaDoCliente(c: Cliente): FichaDaLinha {
  return {
    rotulo: 'Cliente',
    titulo: c.razao_social,
    sub: (
      <>
        {c.nome_fantasia && <span>{c.nome_fantasia}</span>}
        {c.codigo && <span className="mono">{c.codigo}</span>}
      </>
    ),
    corpo: (
      <>
        <div className="tf-selos">
          <Selo tom={TOM[c.status] ?? 'neutro'}>{SITUACAO[c.status] ?? c.status}</Selo>
        </div>
        <CamposDaFicha itens={[
          ['CNPJ / CPF', c.cnpj_cpf
            ? <span className="mono">{documento(c.cnpj_cpf)}</span>
            : <span className="nada">não informado</span>],
          ['Obras', <ObrasDoCliente id={c.id} />],
        ]} />
      </>
    ),
    acoes: <Link className="botao" to={`/cliente/${c.id}`}>Abrir cadastro</Link>,
  };
}
